import * as React from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { Point, ViewSettings, MarkupState, TradeSymbol } from '@shared/types/tsk';

interface Viewport {
    scale: number;
    offset: Point;
}

const OBJECT_SNAP_TOLERANCE_PX = 14;

export const useCanvasCoordinates = (
    containerRef: React.RefObject<HTMLDivElement | null>,
    viewSettings: ViewSettings,
    markupRef: React.RefObject<MarkupState>
) => {
    const [worldSize, setWorldSize] = useState<{ w: number, h: number } | null>(null);
    const [viewport, setViewport] = useState<Viewport>({ scale: 1, offset: { x: 0, y: 0 } });

    const viewportRef = useRef<Viewport>(viewport);

    useEffect(() => {
        viewportRef.current = viewport;
    }, [viewport]);

    const getLocalCoords = useCallback((clientX: number, clientY: number): Point => {
        if (!containerRef.current) return { x: 0, y: 0 };
        const rect = containerRef.current.getBoundingClientRect();
        return { x: clientX - rect.left, y: clientY - rect.top };
    }, [containerRef]);

    const getCoords = useCallback((clientX: number, clientY: number) => {
        const local = getLocalCoords(clientX, clientY);
        const { scale: s, offset: o } = viewportRef.current;
        return {
            screen: local,
            world: { x: (local.x - o.x) / s, y: (local.y - o.y) / s }
        };
    }, [getLocalCoords]);

    const applyZoom = useCallback((newScale: number, focalLocal: Point) => {
        setViewport((prev) => {
            const nextScale = Math.min(Math.max(0.05, newScale), 15);
            const worldX = (focalLocal.x - prev.offset.x) / prev.scale;
            const worldY = (focalLocal.y - prev.offset.y) / prev.scale;
            
            return {
                scale: nextScale,
                offset: {
                    x: focalLocal.x - worldX * nextScale,
                    y: focalLocal.y - worldY * nextScale
                }
            };
        });
    }, []);

    const snapPoint = useCallback((p: Point, options: { excludeId?: string, gridOnly?: boolean } = {}): Point & { attachedSymbolId?: string } => {
        const toleranceWorld = OBJECT_SNAP_TOLERANCE_PX / viewportRef.current.scale;
        let closestSymbol: TradeSymbol | null = null;
        let minDist = toleranceWorld;
        const { excludeId, gridOnly } = options;

        // Capture/Preserve True World Location (Universal Coordinates)
        const ux = p.ux ?? p.x;
        const uy = p.uy ?? p.y;

        if (markupRef.current && !gridOnly) {
            // 1. Check Symbols
            for (const symbol of markupRef.current.symbols) {
                if (excludeId && symbol.id === excludeId) continue;
                const dist = Math.hypot(symbol.position.x - p.x, symbol.position.y - p.y);
                if (dist < minDist) {
                    minDist = dist;
                    closestSymbol = symbol;
                }
            }

            // 2. Check Nodes (Priority check)
            for (const line of markupRef.current.lines) {
                for (const node of line.points) {
                    const dist = Math.hypot(node.x - p.x, node.y - p.y);
                    if (dist < minDist) {
                        minDist = dist;
                        closestSymbol = null; // Node hit overrides symbol hit if closer
                        return { x: node.x, y: node.y, ux, uy }; // Node snap preserves UX/UY
                    }
                }
            }
        }

        if (closestSymbol && !gridOnly) {
            return { ...closestSymbol.position, ux, uy, attachedSymbolId: closestSymbol.id };
        }

        if (viewSettings.snapToGrid) {
            const gs = viewSettings.gridSize;
            return {
                x: Math.round(ux / gs) * gs,
                y: Math.round(uy / gs) * gs,
                ux,
                uy
            };
        }
        return { x: ux, y: uy, ux, uy };
    }, [viewSettings.snapToGrid, viewSettings.gridSize, markupRef]);

    const setViewportClamped = useCallback((v: Viewport | ((prev: Viewport) => Viewport)) => {
        setViewport(prev => {
            const next = typeof v === 'function' ? v(prev) : v;
            return {
                ...next,
                scale: Math.min(Math.max(0.05, next.scale), 15)
            };
        });
    }, []);

    return {
        scale: viewport.scale,
        setScale: (s: number | ((prev: number) => number)) => 
            setViewportClamped(prev => ({ ...prev, scale: typeof s === 'function' ? s(prev.scale) : s })),
        offset: viewport.offset,
        setOffset: (o: Point | ((prev: Point) => Point)) => 
            setViewportClamped(prev => ({ ...prev, offset: typeof o === 'function' ? o(prev.offset) : o })),
        setViewport: setViewportClamped,
        worldSize,
        setWorldSize,
        viewportRef,
        getLocalCoords,
        getCoords,
        applyZoom,
        snapPoint
    };
};
